"use client";

import React, { useEffect, useState } from "react";
import "./PrivateTheatre.css";

const theatreHighlights = [
  {
    icon: "🔒",
    title: "Exclusive Private Space",
    description:
      "Enjoy your screening in a theatre reserved exclusively for you and your group.",
    details:
      "Your booking gives your group a private theatre experience during the reserved slot. Enjoy your movie, celebration or gathering without sharing the theatre with other guests.",
  },
  {
    icon: "✨",
    title: "Pre-Decorated Setup",
    description:
      "Arrive to a pre-installed celebration setup that is ready for your experience. The decor is fixed and not customizable.",
    details:
      "The theatre comes with its existing celebration decoration, prepared before you arrive. Please note that the fixed decor cannot be customized or redesigned. You can personalize the occasion with available add-ons.",
  },
  {
    icon: "🎬",
    title: "Your Movie, Your Moment",
    description:
      "Discuss your preferred movie or OTT content with our team before booking to confirm availability.",
    details:
      "Tell our team which movie or OTT content you would like to watch. Screening availability, access requirements and supported content must be confirmed before your booking.",
  },
  {
    icon: "🎉",
    title: "Perfect for Every Occasion",
    description:
      "Make date nights, birthdays, anniversaries, family outings and group hangouts more memorable.",
    details:
      "Plan a romantic evening, birthday, anniversary, family movie night or friends' gathering. You can combine the private theatre with available food, cakes, gifts and entertainment add-ons.",
  },
];

const audienceTypes = [
  {
    icon: "💑",
    title: "Couples",
    description:
      "Enjoy a private, comfortable setting for date nights, anniversaries and special moments together.",
    details:
      "Create a relaxed experience for two with a private screening and the existing theatre decor. Add a cake, bouquet, customized song or another available extra to make your occasion personal.",
  },
  {
    icon: "👥",
    title: "Friends",
    description:
      "Create your own hangout to watch movies together, celebrate or add gaming to your plan.",
    details:
      "Bring your friends together for a movie night, celebration or gaming session. Ask the team about available group capacity, time slots and entertainment options.",
  },
  {
    icon: "👨‍👩‍👧‍👦",
    title: "Families",
    description:
      "Spend quality time together in a space reserved just for your family.",
    details:
      "Enjoy a private family outing with a movie or suitable entertainment. Confirm guest capacity and content availability with the HexaPlay team before booking.",
  },
  {
    icon: "🎂",
    title: "Celebration Groups",
    description:
      "Make birthdays, bride-to-be parties, proposals and surprises more special in a private setting.",
    details:
      "Gather your friends or family for a special occasion. Cakes, bouquets, gifts, fog entry and other available add-ons can help make the event more memorable.",
  },
];

const addOns = [
  {
    icon: "🎂",
    title: "Cakes",
    description:
      "Add a cake to your birthday, anniversary or surprise celebration. Confirm available flavours, sizes and pricing with the team.",
  },
  {
    icon: "🍟",
    title: "Food & Beverages",
    description:
      "Choose from available snacks and beverages to enjoy during your screening. Menu options and prices are confirmed separately.",
  },
  {
    icon: "💐",
    title: "Bouquets & Gifts",
    description:
      "Make the moment special with available bouquets, gifts and keepsakes suitable for surprises and celebrations.",
  },
  {
    icon: "🌫️",
    title: "Fog Entry",
    description:
      "Add a dramatic fog-entry effect to create a memorable entrance for a celebration or special surprise.",
  },
  {
    icon: "🎵",
    title: "Customized Song",
    description:
      "Ask about creating a personalized song experience inspired by your relationship, occasion or special message.",
  },
  {
    icon: "🎨",
    title: "Canvas Painting",
    description:
      "Explore a creative keepsake that turns a special memory or meaningful moment into artwork.",
  },
  {
    icon: "🎤",
    title: "Karaoke",
    description:
      "Add singing and music to your outing for a lively experience with friends, family or your partner.",
  },
];

const faqs = [
  {
    question: "Is the private theatre only for me and my group?",
    answer:
      "Yes. The private theatre is reserved exclusively for your group during your confirmed booking slot.",
  },
  {
    question: "Can I watch my own movie or OTT content?",
    answer:
      "Please share your preferred movie or OTT content with our team before booking so they can confirm availability and any access requirements.",
  },
  {
    question: "Can I celebrate a birthday or anniversary in the theatre?",
    answer:
      "Yes. You can combine the private theatre experience with available celebration add-ons such as cakes, food, bouquets, gifts and other extras.",
  },
  {
    question: "How much does a private theatre booking cost?",
    answer:
      "Pricing may depend on the selected theatre, slot, group size and add-ons. Open the booking information popup to learn what details to confirm with the HexaPlay team.",
  },
  {
    question: "Can I customize the theatre decorations?",
    answer:
      "The theatre has a pre-installed, fixed decoration setup that is not customizable. You can personalize your occasion by choosing from available add-on experiences.",
  },
  {
    question: "How do I confirm my booking?",
    answer:
      "Contact the HexaPlay team to confirm available theatres, time slots, guest capacity, screening options and current prices before finalizing your reservation.",
  },
];

const informationContent = {
  booking: {
    icon: "📅",
    title: "Plan Your Private Theatre Experience",
    description:
      "Make your next movie night or celebration special in a theatre reserved exclusively for your group.",
    points: [
      "Choose your preferred date and time slot.",
      "Confirm theatre availability and guest capacity.",
      "Discuss your movie or OTT screening requirements.",
      "Ask about current pricing and available add-ons.",
    ],
    note:
      "Your booking is not confirmed through this popup. Please contact the HexaPlay team to verify availability and complete your reservation.",
  },
  addons: {
    icon: "✨",
    title: "Personalize Your Experience",
    description:
      "Enhance your private screening with optional extras that suit your occasion.",
    points: [
      "Cakes and food and beverages.",
      "Bouquets, gifts and special surprises.",
      "Fog entry for a dramatic celebration moment.",
      "Customized songs, canvas painting and karaoke.",
    ],
    note:
      "Add-ons may have separate charges and are subject to availability. The existing theatre decoration remains fixed.",
  },
  contact: {
    icon: "💬",
    title: "Booking Information",
    description:
      "Before reserving your private theatre, get the information you need from the HexaPlay team.",
    points: [
      "Current booking prices and available packages.",
      "Available dates, time slots and duration.",
      "Guest capacity and theatre facilities.",
      "Screening arrangements and add-on availability.",
    ],
    note:
      "Please confirm all booking details and charges with the team before making your reservation.",
  },
};

const PrivateTheatre = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [activePopup, setActivePopup] = useState(null);

  const openInfo = (content) => {
    setActivePopup(content);
  };

  const closeInfo = () => {
    setActivePopup(null);
  };

  useEffect(() => {
    if (!activePopup) return undefined;

    const previousOverflow = document.body.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActivePopup(null);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activePopup]);

  return (
    <main className="private-theatre-page">
      {/* Hero */}
      <section className="pt-hero">
        <div className="pt-hero-glow pt-hero-glow-one" />
        <div className="pt-hero-glow pt-hero-glow-two" />

        <div className="pt-container pt-hero-content">
          <span className="pt-eyebrow">
            YOUR SPACE. YOUR MOVIE. YOUR MOMENT.
          </span>

          <h1>Private Movie Screening Experience at HexaPlay</h1>

          <p className="pt-hero-description">
            Watch your favourite movie or content in a theatre booked only
            for you. At HexaPlay, a private movie screening means your own
            space, your own group and your own time, with a pre-decorated,
            ready-to-celebrate setup. It works for a quiet evening with
            someone special, a hangout with friends, a family outing or as
            the centrepiece of a celebration.
          </p>

          <div className="pt-hero-actions">
            <button
              type="button"
              className="pt-button pt-button-primary"
              onClick={() => openInfo(informationContent.booking)}
            >
              Plan Your Experience
              <span aria-hidden="true">→</span>
            </button>

            <button
              type="button"
              className="pt-button pt-button-outline"
              onClick={() => openInfo(informationContent.addons)}
            >
              Explore Add-Ons
            </button>
          </div>

          <div className="pt-hero-note">
            <span className="pt-note-icon" aria-hidden="true">✦</span>
            A private screening experience designed around your occasion.
          </div>
        </div>
      </section>

      {/* What is a private theatre */}
      <section className="pt-section pt-about-section">
        <div className="pt-container">
          <div className="pt-section-heading">
            <span className="pt-eyebrow">THE EXPERIENCE</span>
            <h2>What Is a Private Theatre?</h2>
            <p>
              A private theatre is a theatre room reserved exclusively for
              your group. Enjoy a more personal screening experience by
              choosing your preferred slot, inviting your group and
              discussing your screening requirements before booking.
            </p>
          </div>

          <div className="pt-highlights-grid">
            {theatreHighlights.map((item) => (
              <article className="pt-highlight-card" key={item.title}>
                <div className="pt-card-icon" aria-hidden="true">
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <button
                  type="button"
                  className="pt-card-more"
                  onClick={() => openInfo(item)}
                >
                  Learn more <span aria-hidden="true">→</span>
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="pt-section pt-audience-section">
        <div className="pt-container">
          <div className="pt-section-heading">
            <span className="pt-eyebrow">MADE FOR YOUR MOMENTS</span>
            <h2>Who Is the Private Theatre For?</h2>
            <p>
              Whether you are planning a romantic evening or a fun group
              outing, make the experience your own.
            </p>
          </div>

          <div className="pt-audience-grid">
            {audienceTypes.map((item) => (
              <article className="pt-audience-card" key={item.title}>
                <span className="pt-audience-icon" aria-hidden="true">
                  {item.icon}
                </span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <button
                  type="button"
                  className="pt-card-more"
                  onClick={() => openInfo(item)}
                >
                  Explore experience <span aria-hidden="true">→</span>
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="pt-section pt-expect-section">
        <div className="pt-container pt-expect-layout">
          <div className="pt-expect-copy">
            <span className="pt-eyebrow">YOUR PRIVATE SCREENING</span>
            <h2>What to Expect at the HexaPlay Private Theatre</h2>
            <p>
              Enjoy a private setting with a ready-to-celebrate atmosphere.
              Our team can help you understand the available theatre
              options, booking slots and screening arrangements before
              you confirm your reservation.
            </p>

            <button
              type="button"
              className="pt-button pt-button-primary"
              onClick={() => openInfo(informationContent.contact)}
            >
              Enquire About Booking
              <span aria-hidden="true">→</span>
            </button>
          </div>

          <div className="pt-expect-list">
            <div className="pt-expect-item">
              <span className="pt-check" aria-hidden="true">✓</span>
              <div>
                <h3>Exclusive private space</h3>
                <p>Reserved for your group during your selected slot.</p>
              </div>
            </div>

            <div className="pt-expect-item">
              <span className="pt-check" aria-hidden="true">✓</span>
              <div>
                <h3>Pre-installed premium decor</h3>
                <p>
                  A fixed, pre-decorated setup ready when you arrive.
                  The decor is not customizable.
                </p>
              </div>
            </div>

            <div className="pt-expect-item">
              <span className="pt-check" aria-hidden="true">✓</span>
              <div>
                <h3>Screen and sound</h3>
                <p>
                  Contact our team to confirm the screen size, sound
                  system and seating arrangements.
                </p>
              </div>
            </div>

            <div className="pt-expect-item">
              <span className="pt-check" aria-hidden="true">✓</span>
              <div>
                <h3>Capacity and slot duration</h3>
                <p>
                  Guest capacity and booking duration can be confirmed
                  with the team before making your reservation.
                </p>
              </div>
            </div>

            <div className="pt-expect-item">
              <span className="pt-check" aria-hidden="true">✓</span>
              <div>
                <h3>Three private theatre options</h3>
                <p>
                  Ask our team about the available theatres and their
                  respective features before choosing your slot.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Movies and OTT */}
      <section className="pt-section pt-movie-section">
        <div className="pt-container">
          <div className="pt-movie-card">
            <div className="pt-movie-icon" aria-hidden="true">
              <span>▶</span>
            </div>

            <div className="pt-movie-copy">
              <span className="pt-eyebrow">PRESS PLAY ON YOUR MOMENT</span>
              <h2>Movies and OTT Content</h2>
              <p>
                You can discuss your preferred movie or OTT content with
                our team before booking. Our team can confirm the available
                screening options and any requirements for your preferred
                content.
              </p>
              <p className="pt-small-note">
                Movie availability, guest OTT login permissions and access
                to new releases should be confirmed with the team before
                your booking.
              </p>

              <button
                type="button"
                className="pt-button pt-button-outline pt-inline-button"
                onClick={() => openInfo(theatreHighlights[2])}
              >
                Screening information <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="pt-section pt-addons-section">
        <div className="pt-container">
          <div className="pt-section-heading">
            <span className="pt-eyebrow">MAKE IT EXTRA SPECIAL</span>
            <h2>Combine the Theatre With Add-Ons</h2>
            <p>
              Turn your screening into a celebration with additional
              experiences. Select an option below to learn more about
              what it can add to your occasion.
            </p>
          </div>

          <div className="pt-addons-grid">
            {addOns.map((item) => (
              <button
                type="button"
                className="pt-addon-chip"
                key={item.title}
                onClick={() => openInfo(item)}
              >
                <span className="pt-addon-icon" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="pt-addon-name">{item.title}</span>
                <span className="pt-addon-arrow" aria-hidden="true">↗</span>
              </button>
            ))}
          </div>

          <div className="pt-addons-footer">
            <p>
              From a simple movie night to a fully planned celebration,
              explore available extras and confirm their prices and
              availability with our team.
            </p>

            <button
              type="button"
              className="pt-button pt-button-primary"
              onClick={() => openInfo(informationContent.addons)}
            >
              Explore All Add-Ons
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pt-section pt-faq-section">
        <div className="pt-container pt-faq-layout">
          <div className="pt-faq-heading">
            <span className="pt-eyebrow">NEED TO KNOW</span>
            <h2>Frequently Asked Questions</h2>
            <p>
              Find answers to common questions about the HexaPlay private
              theatre experience.
            </p>

            <button
              type="button"
              className="pt-button pt-button-outline"
              onClick={() => openInfo(informationContent.contact)}
            >
              Booking Information
            </button>
          </div>

          <div className="pt-faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              const answerId = `pt-faq-answer-${index}`;

              return (
                <article
                  className={`pt-faq-item ${isOpen ? "is-open" : ""}`}
                  key={faq.question}
                >
                  <button
                    type="button"
                    className="pt-faq-question"
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                  >
                    <span>{faq.question}</span>
                    <span className="pt-faq-toggle" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="pt-faq-answer" id={answerId}>
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-final-section">
        <div className="pt-container pt-final-content">
          <span className="pt-eyebrow">YOUR MOMENT STARTS HERE</span>
          <h2>Make Your Next Movie Night Different</h2>
          <p>
            Bring your favourite people together in a private theatre
            and make the occasion yours.
          </p>

          <div className="pt-final-actions">
            <button
              type="button"
              className="pt-button pt-button-light"
              onClick={() => openInfo(informationContent.booking)}
            >
              Plan Your Experience
              <span aria-hidden="true">→</span>
            </button>

            <button
              type="button"
              className="pt-button pt-button-final-outline"
              onClick={() => openInfo(informationContent.addons)}
            >
              View Add-Ons
            </button>
          </div>
        </div>
      </section>

      {/* Reusable information popup */}
      {activePopup && (
        <div
          className="pt-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeInfo();
          }}
        >
          <section
            className="pt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pt-modal-title"
            aria-describedby="pt-modal-description"
          >
            <button
              type="button"
              className="pt-modal-close"
              onClick={closeInfo}
              aria-label="Close popup"
              autoFocus
            >
              ×
            </button>

            <div className="pt-modal-icon" aria-hidden="true">
              {activePopup.icon || "✦"}
            </div>

            <span className="pt-eyebrow">HEXAPLAY EXPERIENCE</span>

            <h2 id="pt-modal-title">{activePopup.title}</h2>

            <p
              className="pt-modal-description"
              id="pt-modal-description"
            >
              {activePopup.details || activePopup.description}
            </p>

            {activePopup.points && (
              <ul className="pt-modal-list">
                {activePopup.points.map((point) => (
                  <li key={point}>
                    <span aria-hidden="true">✓</span>
                    {point}
                  </li>
                ))}
              </ul>
            )}

            {activePopup.note && (
              <p className="pt-modal-note">{activePopup.note}</p>
            )}

            <button
              type="button"
              className="pt-button pt-button-primary pt-modal-done"
              onClick={closeInfo}
            >
              Got It <span aria-hidden="true">✓</span>
            </button>
          </section>
        </div>
      )}
    </main>
  );
};

export default PrivateTheatre;
